import { useNavigate } from 'react-router-dom'
import { useCallback } from 'react'

/**
 * Hook that provides a "back" navigation function.
 * Uses browser history when available, falls back to a specified path otherwise.
 * 
 * This is more robust than manually passing return paths in state because:
 * - Automatically preserves URL query parameters
 * - Works with browser back button expectations
 * - No need to explicitly pass state on every navigation
 * 
 * @param fallbackPath - Path to navigate to if there's no browser history
 * @returns A function that navigates back
 */
export function useNavigateBack(fallbackPath: string = '/') {
  const navigate = useNavigate()
  
  const goBack = useCallback(() => {
    // React Router v6 stores navigation index in history state
    // idx > 0 means we have pages to go back to within our app
    if (window.history.state?.idx > 0) {
      navigate(-1)
    } else {
      // No history (user opened link directly), go to fallback
      navigate(fallbackPath, { replace: true })
    }
  }, [navigate, fallbackPath])
  
  return goBack
}

